/**
 * On-frame drawings attached to review comments ("annotations"): pen strokes,
 * arrows, boxes and ellipses drawn over a paused frame. Coordinates are
 * fractions of the video frame (0..1 on both axes) and stroke widths a
 * fraction of the frame height, so a drawing lines up at any player size, on
 * the review page and over the editor preview alike.
 */

export const ANNOTATION_TOOLS = ['pen', 'arrow', 'rect', 'ellipse'] as const;
export type AnnotationTool = (typeof ANNOTATION_TOOLS)[number];

export interface AnnotationShape {
  tool: AnnotationTool;
  /** #rrggbb */
  color: string;
  /** Stroke width as a fraction of the frame height. */
  width: number;
  /** Frame-relative points: every sample for `pen`, start and end for the others. */
  points: [number, number][];
}

export interface Annotation {
  v: 1;
  shapes: AnnotationShape[];
}

export const ANNOTATION_COLORS = ['#ff3b30', '#ffcc00', '#34c759', '#0a84ff', '#bf5af2', '#ffffff'];
export const ANNOTATION_WIDTHS = { thin: 0.004, medium: 0.008, thick: 0.016 } as const;
export const MAX_ANNOTATION_JSON = 64_000;
const MAX_SHAPES = 200;
const MAX_POINTS = 20_000;
const MIN_WIDTH = 0.001;
const MAX_WIDTH = 0.05;

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));
const round = (n: number) => Math.round(n * 10_000) / 10_000;

/**
 * Validates and normalizes a drawing. Returns null for anything that is not a
 * drawing with at least one shape; out-of-range values are clamped.
 */
export function normalizeAnnotation(input: unknown): Annotation | null {
  const shapesIn = (input as { shapes?: unknown })?.shapes;
  if (!Array.isArray(shapesIn)) return null;
  const shapes: AnnotationShape[] = [];
  let total = 0;
  for (const s of shapesIn.slice(0, MAX_SHAPES)) {
    const tool = (s as AnnotationShape)?.tool;
    if (!ANNOTATION_TOOLS.includes(tool)) continue;
    const color = String((s as AnnotationShape).color ?? '');
    if (!/^#[0-9a-fA-F]{6}$/.test(color)) continue;
    const w = Number((s as AnnotationShape).width);
    const width = Number.isFinite(w) ? Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, w)) : ANNOTATION_WIDTHS.medium;
    const ptsIn = (s as AnnotationShape).points;
    if (!Array.isArray(ptsIn)) continue;
    const points: [number, number][] = [];
    for (const p of ptsIn) {
      if (total + points.length >= MAX_POINTS) break;
      if (!Array.isArray(p) || p.length !== 2) continue;
      const [x, y] = p.map(Number);
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      points.push([round(clamp01(x)), round(clamp01(y))]);
    }
    const kept = tool === 'pen' ? points : points.length >= 2 ? [points[0], points[points.length - 1]] : [];
    if (kept.length < (tool === 'pen' ? 1 : 2)) continue;
    total += kept.length;
    shapes.push({ tool, color: color.toLowerCase(), width: round(width), points: kept });
  }
  return shapes.length ? { v: 1, shapes } : null;
}

/** Parses stored drawing JSON ('' or invalid → null). */
export function parseAnnotation(json: string | null | undefined): Annotation | null {
  if (!json) return null;
  try {
    return normalizeAnnotation(JSON.parse(json));
  } catch {
    return null;
  }
}

/** Normalized JSON for storage ('' when there is nothing to store). Throws when it is too large. */
export function serializeAnnotation(a: unknown): string {
  const n = normalizeAnnotation(a);
  if (!n) return '';
  const json = JSON.stringify(n);
  if (json.length > MAX_ANNOTATION_JSON) throw new Error('The drawing is too detailed');
  return json;
}

/**
 * Thins a freehand stroke (Ramer–Douglas–Peucker): drops points within
 * `tolerance` (frame fractions) of the line through their neighbours.
 */
export function simplifyStroke(points: [number, number][], tolerance = 0.0015): [number, number][] {
  if (points.length <= 2) return points;
  const keep = new Uint8Array(points.length);
  keep[0] = keep[points.length - 1] = 1;
  const stack: [number, number][] = [[0, points.length - 1]];
  while (stack.length) {
    const [a, b] = stack.pop()!;
    const [ax, ay] = points[a];
    const [bx, by] = points[b];
    const dx = bx - ax;
    const dy = by - ay;
    const len = Math.hypot(dx, dy) || 1e-9;
    let best = -1;
    let bestD = tolerance;
    for (let i = a + 1; i < b; i++) {
      const d = Math.abs(dy * points[i][0] - dx * points[i][1] + bx * ay - by * ax) / len;
      if (d > bestD) {
        bestD = d;
        best = i;
      }
    }
    if (best > 0) {
      keep[best] = 1;
      stack.push([a, best], [best, b]);
    }
  }
  return points.filter((_, i) => keep[i]);
}

/** The subset of CanvasRenderingContext2D that drawAnnotation uses. */
export interface AnnotationCanvas {
  strokeStyle: unknown;
  lineWidth: number;
  lineCap: unknown;
  lineJoin: unknown;
  beginPath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  quadraticCurveTo(cpx: number, cpy: number, x: number, y: number): void;
  ellipse(x: number, y: number, rx: number, ry: number, rotation: number, start: number, end: number): void;
  rect(x: number, y: number, w: number, h: number): void;
  stroke(): void;
}

function tracePath(ctx: AnnotationCanvas, s: AnnotationShape, w: number, h: number, lw: number) {
  const pts = s.points.map(([x, y]) => [x * w, y * h] as const);
  ctx.beginPath();
  if (s.tool === 'pen') {
    if (pts.length === 1) {
      ctx.moveTo(pts[0][0], pts[0][1]);
      ctx.lineTo(pts[0][0] + 0.01, pts[0][1]);
      return;
    }
    // Smooth through the midpoints between samples.
    ctx.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < pts.length - 1; i++) {
      const mx = (pts[i][0] + pts[i + 1][0]) / 2;
      const my = (pts[i][1] + pts[i + 1][1]) / 2;
      ctx.quadraticCurveTo(pts[i][0], pts[i][1], mx, my);
    }
    ctx.lineTo(pts[pts.length - 1][0], pts[pts.length - 1][1]);
    return;
  }
  const [[x0, y0], [x1, y1]] = [pts[0], pts[pts.length - 1]];
  if (s.tool === 'rect') {
    ctx.rect(Math.min(x0, x1), Math.min(y0, y1), Math.abs(x1 - x0), Math.abs(y1 - y0));
  } else if (s.tool === 'ellipse') {
    ctx.ellipse((x0 + x1) / 2, (y0 + y1) / 2, Math.abs(x1 - x0) / 2, Math.abs(y1 - y0) / 2, 0, 0, Math.PI * 2);
  } else {
    // Arrow: shaft plus a head scaled with the stroke.
    const ang = Math.atan2(y1 - y0, x1 - x0);
    const head = Math.min(Math.hypot(x1 - x0, y1 - y0) * 0.5, lw * 4 + 6);
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y1);
    ctx.moveTo(x1 - head * Math.cos(ang - Math.PI / 7), y1 - head * Math.sin(ang - Math.PI / 7));
    ctx.lineTo(x1, y1);
    ctx.lineTo(x1 - head * Math.cos(ang + Math.PI / 7), y1 - head * Math.sin(ang + Math.PI / 7));
  }
}

/**
 * Draws a drawing over a w×h frame. Each shape gets a faint dark outline
 * first, so it stays readable on light and dark footage.
 */
export function drawAnnotation(ctx: AnnotationCanvas, a: Annotation | null, w: number, h: number) {
  if (!a) return;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (const s of a.shapes) {
    const lw = Math.max(1, s.width * h);
    tracePath(ctx, s, w, h, lw);
    ctx.strokeStyle = 'rgba(0,0,0,0.45)';
    ctx.lineWidth = lw + Math.max(2, lw * 0.5);
    ctx.stroke();
    tracePath(ctx, s, w, h, lw);
    ctx.strokeStyle = s.color;
    ctx.lineWidth = lw;
    ctx.stroke();
  }
}

/** Export ids on a review link, oldest first (`versions` JSON, or just its `jobId`). */
export function reviewLinkVersions(link: { jobId: string; versions?: string }): string[] {
  if (link.versions) {
    try {
      const v = JSON.parse(link.versions);
      if (Array.isArray(v) && v.length && v.every((x) => typeof x === 'string')) return v;
    } catch {
      /* fall back */
    }
  }
  return link.jobId ? [link.jobId] : [];
}
